import { useQuery } from '@tanstack/react-query';
import { recipeRequests, type RecipeSearchParams } from '@/lib/api/requests/recipes';
import type { PaginationParams } from '@/types/common';
import { recipeKeys } from './keys';
import type { CostSummary } from '@/lib/api/requests/recipes';
import type { RecipeIngredient } from '@/types/recipe';

export function useRecipes(params?: RecipeSearchParams, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.list(params),
    queryFn: () => recipeRequests.list(params),
    enabled,
    placeholderData: (previousData) => previousData,
  });
}

export function useRecipe(id: number, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.detail(id),
    queryFn: () => recipeRequests.getById(id),
    enabled: !!id && enabled,
  });
}

export function useRecipeCategories(enabled = true, companyId?: number) {
  return useQuery({
    queryKey: [...recipeKeys.categories(), companyId ?? null],
    queryFn: () => recipeRequests.getCategories(companyId),
    enabled,
    staleTime: 10 * 60 * 1000,
    // Without this, switching concepts re-keys the query and `data` reverts to
    // `undefined` until the new list loads — the chip row collapses to just
    // "All" for a beat, so the previously selected section chip loses its
    // highlighted state even though the `category` search param never changed.
    placeholderData: (previousData) => previousData,
  });
}


export function useRecipeAllergens(id: number, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.allergens(id),
    queryFn: () => recipeRequests.getAllergens(id),
    enabled: !!id && enabled,
  });
}

export function useRecipeIngredients(recipeId: number, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.ingredients(recipeId),
    queryFn: () => recipeRequests.getIngredients(recipeId),
    enabled: !!recipeId && enabled,
  });
}

export function useRecipeShelfLife(recipeId: string, params?: PaginationParams, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.shelfLife(recipeId, params),
    queryFn: () => recipeRequests.getShelfLife(recipeId, params),
    enabled: !!recipeId && enabled,
  });
}

export function useCostSummary(recipeId: number, enabled = true) {
  return useQuery<CostSummary>({
    queryKey: recipeKeys.costSummary(recipeId),
    queryFn: () => recipeRequests.getCostSummary(recipeId),
    enabled: !!recipeId && enabled,
  });
}

/** The method's stages, for the recipe card views. */
export function useRecipeStages(recipeId: number, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.stages(recipeId),
    queryFn: () => recipeRequests.getStages(recipeId),
    enabled: !!recipeId && enabled,
  });
}

/**
 * Every line of a recipe, for views that lay out the whole method at once.
 * The list endpoint pages (100 at most per page), so this follows the pages
 * to the end rather than showing a recipe with its later lines missing.
 */
export function useAllRecipeIngredients(recipeId: number, enabled = true) {
  return useQuery({
    queryKey: [...recipeKeys.ingredients(recipeId), 'all'] as const,
    queryFn: async () => {
      // The first page says how many there are; every one is read, so the
      // views never show a recipe with lines silently missing.
      const first = await recipeRequests.getIngredients(recipeId, { limit: 100, page: 1 });
      const data: RecipeIngredient[] = [...first.data];
      const totalPages = first.meta?.totalPages ?? 1;
      for (let page = 2; page <= totalPages; page++) {
        const result = await recipeRequests.getIngredients(recipeId, { limit: 100, page });
        data.push(...result.data);
      }
      return { data };
    },
    enabled: !!recipeId && enabled,
  });
}

/** The recipes that use an ingredient ("Used in recipes" on the ingredient page). */
export function useMaterialUsage(materialId: number, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.materialUsage(materialId),
    queryFn: () => recipeRequests.getMaterialUsage(materialId),
    enabled: !!materialId && enabled,
  });
}

/** The recipes a prep recipe goes into. */
export function useRecipeUsedIn(recipeId: number, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.usedIn(recipeId),
    queryFn: () => recipeRequests.getUsedIn(recipeId),
    enabled: !!recipeId && enabled,
  });
}

export function useRecipeStorageContainers(recipeId: number, params?: PaginationParams, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.storageContainers(recipeId, params),
    queryFn: () => recipeRequests.getStorageContainers(recipeId, params),
    enabled: !!recipeId && enabled,
  });
}

export function useRecipeTools(recipeId: number, params?: PaginationParams, enabled = true) {
  return useQuery({
    queryKey: recipeKeys.tools(recipeId, params),
    queryFn: () => recipeRequests.getTools(recipeId, params),
    enabled: !!recipeId && enabled,
  });
}
